import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../Button';
import FormRow from '../../Form/FormRow';
import FormColumn from '../../Form/FormColumn';
import LoanCardTooltip from '../../LoanCardTooltip';

import { formatCurrency } from '../../../utils/formatCurrency';

import './style.scss';

const LoanCard = ({ loanDetails, onSelect, workflowType }) => {
  const handleSelect = () => {
    onSelect(loanDetails);
  };

  const renderAprStr = (item) => (Number(item.originationPercentage) ? 'NO INTEREST' : '0% APR');

  return (
    <div className={`offer-card-card ${loanDetails.isAutoPayOffer ? 'border-light' : 'border-gray'}`}>
      {loanDetails.isAutoPayOffer && (
        <div className="card-header">
          Recommended Offer
        </div>
      )}
      <div className="card-content">
        <section className="top-section">
          <FormRow className="py-3">
            <FormColumn>
              <h2 className="payment-amount mb-0" style={{ color: loanDetails.isAutoPayOffer ? '#0084F6' : '#1C1C1C' }}>
                {`$${formatCurrency(loanDetails.paymentAmount, 2) || '-'}`}
              </h2>
              <h5 className="text-center payment-description">Est. Monthly Payment</h5>
            </FormColumn>
            <FormColumn>
              {
                (!!Number(loanDetails.zipTerm) || !loanDetails.isAutoPayOffer) ? (
                  <>
                    <h2 className={`mb-0 no-interest ${loanDetails.isAutoPayOffer ? '' : 'PriceCard-apr-right'}`} style={{ color: loanDetails.isAutoPayOffer ? '#0084F6' : '#1C1C1C' }}>
                      {loanDetails.isAutoPayOffer ? renderAprStr(loanDetails) : 'No Promotion'}
                    </h2>
                    <h5 className="no-interest" style={{ color: '#000a2f' }}>
                      {loanDetails.isAutoPayOffer ? `If repaid within ${loanDetails.zipTerm} months* and enrolled in AutoPay` : 'Without AutoPay Enrollment'}
                    </h5>
                  </>
                ) : null
              }
            </FormColumn>
          </FormRow>
        </section>
        <section className="bottom-section py-3">
          {workflowType !== 'text2apply' ? (
            <FormRow className="mb-1">
              <FormColumn className="label" offset={8}>
                <h5>Amount of Sale</h5>
              </FormColumn>
              <FormColumn className="value" offset={4}>
                <span style={{ color: loanDetails.isAutoPayOffer ? '#0084F6' : '#000A2F' }}>{`$${formatCurrency(loanDetails.amountFinanced, 2)}`}</span>
              </FormColumn>
            </FormRow>
          ) : null}
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>
              <h5>Amount Financed</h5>
            </FormColumn>
            <FormColumn className="value" offset={4}>
              <span style={{ color: workflowType === 'text2apply' && loanDetails.isAutoPayOffer ? '#0084F6' : '#000A2F' }}>{`$${formatCurrency(loanDetails.amountTaken, 2)}`}</span>
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>Loan Term</FormColumn>
            <FormColumn className="value" offset={4}>
              {`${loanDetails.term} Months`}
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className="label" offset={8}>Interest Rate</FormColumn>
            <FormColumn className="value" offset={4}>
              {`${formatCurrency(loanDetails.rate, 2)}%`}
            </FormColumn>
          </FormRow>
          <FormRow className="mb-1">
            <FormColumn className={`label ${workflowType === 'text2apply' ? '' : 'bigger'}`} offset={8}>
              Est. APR
            </FormColumn>
            <FormColumn offset={4} className={`value ${workflowType === 'text2apply' ? '' : 'bigger'}`}>
              {`${formatCurrency(loanDetails.apr, 4)}%`}
            </FormColumn>
          </FormRow>
          {workflowType === 'text2apply' && !!Number(loanDetails.originationPercentage) ? (
            <FormRow className="no-gutters">
              <FormColumn offset={8} className="label origination-field">
                Origination Fee
                <LoanCardTooltip title="Origination Fee" className="origination-fee-tooltip">
                  The Origination Fee may be up to 8% of the Amount Financed, is non-refundable, and is considered earned upon the funding of your loan. You can calculate the dollar amount of the Origination Fee for each offer listed above by multiplying the Amount Financed figure for that offer by .08 if the Origination Fee is 8%, by .05 if it is 5%, etc. For example, if the Origination Fee is 8% and if the Amount Financed is $1,000 the corresponding Origination Fee would be $80 ($1000 x .08 = $80). The total Principal Amount of your loan is the sum of the Amount Financed and the Origination Fee (i.e. if the Origination Fee is 8% and if the Amount Financed is $1,000 the Principal Amount of the loan would be $1080). Please review your Loan Agreement for additional terms and conditions.
                </LoanCardTooltip>
                <br />
                <small>Included in the monthly payment</small>
              </FormColumn>
              <FormColumn offset={4} className="value">
                {`${formatCurrency(Number(loanDetails.originationPercentage) * 100, 2) || '-'}%`}
              </FormColumn>
            </FormRow>
          ) : null}
        </section>
        <section className="select-button py-3">
          <FormRow>
            <FormColumn>
              <Button
                className={`btn ${loanDetails.isAutoPayOffer ? 'btn-primary' : 'btn-secondary'}`}
                onClick={handleSelect}
                title={loanDetails.isAutoPayOffer ? 'Continue with Autopay' : (
                  <span style={{ textUnderlineOffset: 2 }}>
                    Continue
                    {' '}
                    <u>without</u>
                    {' '}
                    Autopay
                  </span>
                )}
              />
            </FormColumn>
          </FormRow>
        </section>
        <section>
          {loanDetails.downPaymentRequired ? (
            <FormRow className={`downpayment-section no-gutters ${!loanDetails.isAutoPayOffer ? 'downpayment-section-grey' : ''}`}>
              <FormColumn>
                <p>
                  Down Payment
                  <sup>1</sup>
                </p>
              </FormColumn>
              <FormColumn>
                <p className="text-right">{`$${formatCurrency(loanDetails.downPaymentRequired || 0, 2)}`}</p>
              </FormColumn>
            </FormRow>
          ) : null}
        </section>
      </div>
    </div>
  );
};

LoanCard.propTypes = {
  loanDetails: PropTypes.object.isRequired,
  onSelect: PropTypes.func.isRequired,
  workflowType: PropTypes.string.isRequired,
};

export default LoanCard;
